import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { creditText, errText, platformApi, type NewTenant } from "@/admin/api";
import { FilterButtons, Pager, SearchBox, TenantStatusPill } from "@/admin/components";

const STATUS_FILTERS = [
  { value: "", label: "All" },
  { value: "active", label: "Active" },
  { value: "suspended", label: "Suspended" },
  { value: "cancelled", label: "Cancelled" },
];

export default function AdminTenantsPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [addOpen, setAddOpen] = useState(false);

  const q = useQuery({
    queryKey: ["admin", "tenants", page, search, status],
    queryFn: () => platformApi.tenants({ page, search: search || undefined, status: status || undefined }),
    placeholderData: keepPreviousData,
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Tenants</h1>
          <p className="text-sm text-muted-foreground">
            Every workspace on the platform. Sign-up is invite-only, so new tenants are created here.
          </p>
        </div>
        <Button onClick={() => setAddOpen(true)}>
          <Plus className="mr-2 h-4 w-4" /> Add tenant
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <SearchBox
          value={search}
          onChange={(v) => {
            setSearch(v);
            setPage(1);
          }}
          placeholder="Search name or slug"
        />
        <FilterButtons
          options={STATUS_FILTERS}
          value={status}
          onChange={(v) => {
            setStatus(v);
            setPage(1);
          }}
        />
      </div>

      <Card>
        <CardContent className="p-0">
          {q.isLoading ? (
            <div className="space-y-2 p-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : (q.data?.results ?? []).length === 0 ? (
            <p className="p-8 text-center text-sm text-muted-foreground">No tenants match.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="px-4 py-3 font-medium">Tenant</th>
                    <th className="px-4 py-3 font-medium">Owner</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Plan</th>
                    <th className="px-4 py-3 font-medium text-right">Credit</th>
                    <th className="px-4 py-3 font-medium text-right">Users</th>
                    <th className="px-4 py-3 font-medium">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {q.data!.results.map((t) => (
                    <tr key={t.id} className="border-b last:border-0">
                      <td className="px-4 py-3">
                        <Link to={`/admin/tenants/${t.id}`} className="font-medium hover:underline">
                          {t.name}
                        </Link>
                        <div className="text-xs text-muted-foreground">{t.slug}</div>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{t.owner_email ?? "—"}</td>
                      <td className="px-4 py-3">
                        <TenantStatusPill status={t.status} />
                      </td>
                      <td className="px-4 py-3">{t.plan_name ?? "—"}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{creditText(t.credit_balance_millicents)}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{t.users_count}</td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {new Date(t.created_at).toLocaleDateString(undefined, { dateStyle: "medium" })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Pager data={q.data} onPage={setPage} />
        </CardContent>
      </Card>

      {addOpen && <AddTenantDialog onClose={() => setAddOpen(false)} />}
    </div>
  );
}

function AddTenantDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [form, setForm] = useState<NewTenant>({ name: "" });
  const [withOwner, setWithOwner] = useState(true);
  const set = (k: keyof NewTenant) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const create = useMutation({
    mutationFn: () => {
      const body: NewTenant = { name: form.name, slug: form.slug, billing_email: form.billing_email };
      if (withOwner) {
        Object.assign(body, {
          owner_email: form.owner_email,
          owner_password: form.owner_password,
          owner_first_name: form.owner_first_name,
          owner_last_name: form.owner_last_name,
        });
      }
      return platformApi.createTenant(body);
    },
    onSuccess: (t) => {
      toast.success(`Tenant "${t.name}" created.`);
      qc.invalidateQueries({ queryKey: ["admin"] });
      navigate(`/admin/tenants/${t.id}`);
    },
    onError: (e) => toast.error(errText(e, "Couldn't create the tenant.")),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add tenant</DialogTitle>
          <DialogDescription>
            The tenant gets the default limits and platform billing straight away.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 space-y-1.5">
              <Label>Name</Label>
              <Input value={form.name} onChange={set("name")} required />
            </div>
            <div className="space-y-1.5">
              <Label>Slug</Label>
              <Input value={form.slug ?? ""} onChange={set("slug")} placeholder="From the name" />
            </div>
            <div className="space-y-1.5">
              <Label>Billing email</Label>
              <Input type="email" value={form.billing_email ?? ""} onChange={set("billing_email")} placeholder="Owner's email" />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={withOwner} onChange={(e) => setWithOwner(e.target.checked)} />
            Create the owner login too
          </label>

          {withOwner && (
            <div className="grid grid-cols-2 gap-3 rounded-md border p-3">
              <div className="space-y-1.5">
                <Label>First name</Label>
                <Input value={form.owner_first_name ?? ""} onChange={set("owner_first_name")} />
              </div>
              <div className="space-y-1.5">
                <Label>Last name</Label>
                <Input value={form.owner_last_name ?? ""} onChange={set("owner_last_name")} />
              </div>
              <div className="col-span-2 space-y-1.5">
                <Label>Email</Label>
                <Input type="email" value={form.owner_email ?? ""} onChange={set("owner_email")} required />
              </div>
              <div className="col-span-2 space-y-1.5">
                <Label>Password</Label>
                <Input type="password" value={form.owner_password ?? ""} onChange={set("owner_password")} required autoComplete="new-password" />
                <p className="text-xs text-muted-foreground">Their email is marked verified, so they can log in right away.</p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? "Creating…" : "Create tenant"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
